import { STAFF_ALERT_KINDS, STAFF_ALERT_LABELS, type StaffAlertPref, type StaffAlertView, UNFINISHED_ALERT_LABEL } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, get, put } from "../api";
import { Alert, Checkbox, Loading, Panel, PanelHeader } from "./ui";

/** Texts and emails about your own visits: booked, cancelled, moved. Saved as each box is ticked. */
export function StaffAlertsPanel({ staffId, title, description, className }: { staffId: string; title: string; description: string; className?: string }) {
  const qc = useQueryClient();
  const key = ["staff-alerts", staffId];
  const q = useQuery({ queryKey: key, queryFn: () => get<StaffAlertView>(`/api/staff/${staffId}/alerts`) });
  const save = useMutation({ mutationFn: (v: StaffAlertPref) => put<StaffAlertView>(`/api/staff/${staffId}/alerts`, v), onSuccess: (v) => qc.setQueryData(key, v) });
  const p: StaffAlertPref | undefined = save.variables && save.isPending ? save.variables : q.data;
  const canChooseAll = q.data?.canChooseAll ?? false;
  const value = (v: StaffAlertPref): StaffAlertPref => ({ sms: v.sms, email: v.email, scope: v.scope });
  return (
    <Panel className={className}>
      <PanelHeader title={title} description={description} />
      <div className="px-5 py-4 text-sm">
        {!p ? (
          q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Loading />
        ) : (
          <table className="w-full max-w-xl">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-muted">
                <th className="pb-2 font-bold" />
                {canChooseAll && <th className="w-40 pb-2 font-bold">Whose visits</th>}
                <th className="w-20 pb-2 font-bold">Text</th>
                <th className="w-20 pb-2 font-bold">Email</th>
              </tr>
            </thead>
            <tbody>
              {STAFF_ALERT_KINDS.map((k) => (
                <tr key={k} className="border-t border-mist">
                  <td className="py-2 pr-2">{STAFF_ALERT_LABELS[k]}</td>
                  {canChooseAll && (
                    <td className="py-2 pr-2">
                      <select
                        aria-label={`Whose visits: ${STAFF_ALERT_LABELS[k]}`}
                        className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                        value={p.scope[k]}
                        onChange={(e) => save.mutate(value({ ...p, scope: { ...p.scope, [k]: e.target.value as "mine" | "all" } }))}
                      >
                        <option value="mine">Mine</option>
                        <option value="all">All visits</option>
                      </select>
                    </td>
                  )}
                  {(["sms", "email"] as const).map((ch) => (
                    <td key={ch} className="py-2">
                      <Checkbox label="" checked={p[ch][k]} onChange={(on) => save.mutate(value({ ...p, [ch]: { ...p[ch], [k]: on } }))} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-mist">
                <td className="py-2 pr-2">{UNFINISHED_ALERT_LABEL}</td>
                {canChooseAll && <td className="py-2 pr-2 text-muted">Every visit</td>}
                {(["sms", "email"] as const).map((ch) => (
                  <td key={ch} className="py-2">
                    <Checkbox label="" checked={!!p[ch].unfinished} onChange={(on) => save.mutate(value({ ...p, [ch]: { ...p[ch], unfinished: on } }))} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Panel>
  );
}
